import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Dropdown,
  Menu,
  Card,
  Button,
  Row,
  Col,
  Statistic,
  Modal,
  Input,
  message,
  Badge,
  Tooltip,
  Popover,
  Icon,
  Select,
  Checkbox,
  Skeleton,
} from "antd";
import moment from "moment";
import fileDownload from "js-file-download";

import "moment/locale/pt-br";

import { toBRL } from "../../../components/UI/InputCurrency";
import { request } from "../../../components/Instance";
import * as montlyPaymentActions from "~/screens/Financial/MonthlyPayment/actions";
import {
  ModuleHeader,
  ModuleHeaderWrapper,
} from "~/components/ModuleHeader";
import DrawerMonthlyPayment from "~/containers/DrawerMonthlyPayment";
import TableList from "./Components/TableList";
import PulseBadge from "~/components/PulseBadge";
import SelectionToolbar from "~/_shared/components/SelectionToolbar";
import Search from "~/_shared/components/Search";

import MonthPicker from "~/_shared/components/MonthPicker";
import GenerateSummaryModal from "./Components/GenerateSummaryModal";
import ReconciliationInfoDrawer from "./Components/ReconciliationInfoDrawer";
import StatTooltip from "~/_shared/components/StatTooltip";
import FilterPillsBar from "~/_shared/components/FilterPillsBar";
import RestoredFiltersNotice from "~/_shared/components/RestoredFiltersNotice";
import usePersistedFilters from "~/_shared/hooks/usePersistedFilters";
import {
  MONTHLY_PAYMENT_FILTERS_KEY,
  getDefaultMonthlyPaymentFilters,
  toMonthlyPaymentsRequest,
  isSingleWholeMonth,
  getPeriodLabel,
  isPeriodDefault,
  countActiveFilters,
} from "./helpers";

const MonthlyPaymentList = () => {
  const [drawerPay, setDrawerPay] = useState(null);
  const [isGenerateLoading, setIsGenerateLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [highlightedIds, setHighlightedIds] = useState({ created: [], updated: {} });
  const [generateSummary, setGenerateSummary] = useState(null);
  const [reconciliationDrawerId, setReconciliationDrawerId] = useState(null);

  var dispatch = useDispatch();

  // Split selectors to prevent unnecessary re-renders
  var data = useSelector(function(state) { return state.monthlyPayment.data; });
  var user = useSelector(function(state) { return state.user; });
  var isLoading = useSelector(function(state) { return state.monthlyPayment.isLoading; });
  var customers = useSelector(function(state) { return state.customers.list; });

  // Applied filters: kept across refreshes for a few minutes (per tenant/user/page).
  // The redux monthlyPayment.filter (restored from the IndexedDB cache with no expiry)
  // is not read here; it is kept in sync by the loads below for other components.
  var persisted = usePersistedFilters(MONTHLY_PAYMENT_FILTERS_KEY, getDefaultMonthlyPaymentFilters);
  var applied = persisted.filters;
  var setApplied = persisted.setFilters;
  var defaults = persisted.defaults;
  var search = applied.search || '';
  var onlyNotReajusted = !!applied.onlyNotReajusted;

  var filter = useMemo(function() {
    return toMonthlyPaymentsRequest(applied);
  }, [applied.from, applied.to, applied.status, applied.filter_by, applied.customer_id]);

  // The only list load: on mount with the restored filters, then whenever they change.
  useEffect(function() {
    dispatch(montlyPaymentActions.loadPayments(filter));
  }, [filter, dispatch]);

  var setSearch = useCallback(function(value) {
    setApplied({ search: value });
  }, [setApplied]);

  var setOnlyNotReajusted = useCallback(function(value) {
    setApplied({ onlyNotReajusted: !!value });
  }, [setApplied]);

  var onAction = useCallback(function(e, record) {
    switch (e.key) {
      case "pay":
        setDrawerPay(record);
        break;
      case "inadimplente":
        Modal.confirm({
          title: "Você realmente deseja marcar como inadimplente?",
          content: "Você poderá alterar isso mais tarde.",
          okText: "Sim",
          okType: "danger",
          cancelText: "Cancelar",
          onOk: function() { dispatch(montlyPaymentActions.markAsDefaulting(record.id)); }
        });
        break;
      case "ajuizado":
        Modal.confirm({
          title: "Você realmente deseja marcar como ajuizado?",
          content: "Você poderá alterar isso mais tarde.",
          okText: "Sim",
          okType: "danger",
          cancelText: "Cancelar",
          onOk: function() { dispatch(montlyPaymentActions.markAsProcessed(record.id)); }
        });
        break;
      case "delete":
        Modal.confirm({
          title: "Você realmente deseja excluir esta mensalidade?",
          content: "Mensalidades excluídas são geradas automaticamente após gerar novamente as mensalidades mensais.",
          okText: "Sim",
          okType: "danger",
          cancelText: "Cancelar",
          onOk: function() { dispatch(montlyPaymentActions.deleteHonorary(record.id)); }
        });
        break;
    }
  }, [dispatch]);

  var changeAmount = useCallback(function(key, value) {
    setDrawerPay(function(prevState) {
      if (!prevState) return prevState;
      var diff = prevState.paid > 0 ? prevState.paid - value : 0;
      return {
        ...prevState,
        amount: value,
        interest: diff > 0 ? diff : 0,
        discount: diff < 0 ? diff : 0
      };
    });
  }, []);

  var changePaid = useCallback(function(key, value) {
    setDrawerPay(function(prevState) {
      if (!prevState) return prevState;
      var diff = value > 0 ? value - prevState.amount : 0;
      return {
        ...prevState,
        paid: value,
        interest: diff > 0 ? diff : 0,
        discount: diff < 0 ? diff : 0
      };
    });
  }, []);

  // Updates the applied filters; the load effect above fetches the list.
  var applyFilter = useCallback(function(overrides) {
    var next = {};
    if (overrides.from) next.from = overrides.from;
    if (overrides.to) next.to = overrides.to;
    if (overrides.status !== undefined) next.status = overrides.status;
    if (overrides.filter_by !== undefined) next.filter_by = overrides.filter_by;
    if (overrides.customer_id !== undefined) next.customer_id = overrides.customer_id;
    setApplied(next);
  }, [setApplied]);

  var handleResetPeriod = useCallback(function() {
    setApplied({ from: defaults.from, to: defaults.to });
  }, [defaults, setApplied]);

  var resetFilters = persisted.resetFilters;
  var handleClearFilters = useCallback(function() {
    resetFilters();
  }, [resetFilters]);

  var handleMonthChange = useCallback(function(newDate) {
    var m = moment(newDate, "YYYY-MM");
    applyFilter({
      from: m.clone().startOf("month").format("YYYY-MM-DD"),
      to: m.clone().endOf("month").format("YYYY-MM-DD"),
    });
  }, [applyFilter]);

  var handleGenerate = useCallback(function(needReajustCount) {
    // Block generation for periods spanning more than 1 month
    var fromMonth = moment(filter.from).format('YYYY-MM');
    var toMonth = moment(filter.to).format('YYYY-MM');
    if (fromMonth !== toMonth) {
      Modal.warning({
        title: 'Período inválido',
        content: 'A geração de mensalidades deve ser feita para apenas 1 mês por vez. Selecione um único mês no filtro de período.',
        okText: 'Entendi',
      });
      return;
    }

    function generate() {
      setIsGenerateLoading(true);
      setHighlightedIds({ created: [], updated: {} });
      dispatch(
        montlyPaymentActions.monthlyGenerate({
          from: filter.from,
          to: filter.to
        })
      )
        .then(function(response) {
          var responseData = response && response.payload && response.payload.data;
          var created = (responseData && responseData.created) || [];
          var updated = (responseData && responseData.updated) || [];

          // Build highlightedIds for row coloring
          var createdIds = created.map(function(r) { return r.id; });
          var updatedMap = {};
          updated.forEach(function(r) {
            updatedMap[r.id] = r;
          });
          setHighlightedIds({ created: createdIds, updated: updatedMap });

          var total = (responseData && responseData.total) || 0;

          // Always show summary modal
          setGenerateSummary({ created: created, updated: updated, total: total });

          dispatch(montlyPaymentActions.loadPayments(filter));
        })
        .finally(function() {
          setIsGenerateLoading(false);
        });
    }

    if (needReajustCount > 0) {
      Modal.confirm({
        title: "Existem mensalidades que precisam ser reajustadas",
        content: "Deseja continuar mesmo assim?",
        okText: "Sim",
        okType: "primary",
        cancelText: "Não",
        onOk: generate
      });
    } else {
      generate();
    }
  }, [dispatch, filter]);

  var handleExportToExcel = useCallback(function() {
    request(
      "POST",
      "/monthly/export/excel",
      {
        filter_by: filter.filter_by,
        from: filter.from,
        to: filter.to,
        ids: selectedRowKeys,
      },
      true,
      {
        responseType: "blob",
      }
    )
      .then(function(response) {
        var formattedFrom = moment(filter.from).format("DD-MM-YYYY");
        var formattedTo = moment(filter.to).format("DD-MM-YYYY");
        fileDownload(
          response.data,
          "Mensalidades_" + formattedFrom + "_" + formattedTo + ".xlsx"
        );
      })
      .catch(function() {
        message.error("Não foi possível realizar a exportação!");
      });
  }, [filter, selectedRowKeys]);

  var handleBulkPrint = useCallback(function() {
    var fileWillBeDownloaded = selectedRowKeys.length <= 10;
    request(
      "POST",
      "/monthly/payments/print/batch",
      { ids: selectedRowKeys },
      true,
      { responseType: fileWillBeDownloaded ? "blob" : "json" }
    )
      .then(function(response) {
        if (fileWillBeDownloaded) {
          fileDownload(response.data, "mensalidades.zip");
        } else {
          message.success(response.data.message);
        }
      })
      .catch(function() {
        message.error("Não foi possível imprimir as mensalidades!");
      });
  }, [selectedRowKeys]);

  var handleBulkDelete = useCallback(function() {
    setDeleteConfirmText("");
    setShowDeleteModal(true);
  }, []);

  var handleBulkDeleteConfirm = useCallback(function() {
    dispatch(montlyPaymentActions.bulkDeleteHonorary(selectedRowKeys))
      .then(function(response) {
        var deletedCount = response.payload.data.data.deleted_count;
        message.success(deletedCount + " mensalidade(s) excluída(s) com sucesso");
        setSelectedRowKeys([]);
        setShowDeleteModal(false);
        setDeleteConfirmText("");
      })
      .catch(function() {
        message.error("Erro ao excluir mensalidades");
      });
  }, [dispatch, selectedRowKeys]);

  var handleClearSelection = useCallback(function() {
    setSelectedRowKeys([]);
  }, []);

  var handleSearchChange = useCallback(function(e) {
    setSearch(e.target.value);
  }, []);

  // Memoized filtered data — single pass, no customersById dependency
  var filtered = useMemo(function() {
    if (!Array.isArray(data)) return [];

    var searchLower = search.toLowerCase();
    var now = new Date();
    var nowYear = now.getFullYear();
    var nowMonth = now.getMonth();
    var result = [];

    for (var i = 0, len = data.length; i < len; i++) {
      var item = data[i];
      var customer = item.customer || {};

      // Search filter
      var nameMatch = customer.name && customer.name.toLowerCase().indexOf(searchLower) !== -1;
      var codMatch = customer.cod !== null && customer.cod !== undefined &&
        customer.cod.toString().toLowerCase().indexOf(searchLower) !== -1;
      if (!nameMatch && !codMatch) continue;

      // Compute months_since_last_honorary from backend-provided latest_honorary_at
      var months_since = null;
      if (item.latest_honorary_at) {
        var d = new Date(item.latest_honorary_at);
        months_since = (nowYear - d.getFullYear()) * 12 + (nowMonth - d.getMonth());
      }

      // onlyNotReajusted filter
      if (onlyNotReajusted && !(months_since >= 12)) continue;

      result.push({
        id: item.id,
        customer_id: item.customer_id,
        customer: customer,
        status: item.status,
        cod: item.cod,
        apuration_at: item.apuration_at,
        due_at: item.due_at,
        method: item.method,
        paid_at: item.paid_at,
        amount: item.amount,
        additionals_amount: item.additionals_amount,
        additionals: item.additionals,
        paid: item.paid,
        paid13: item.paid13,
        interest: item.interest,
        discount: item.discount,
        amount13: item.amount13,
        amount13_breakdown: item.amount13_breakdown,
        is_deleted: item.is_deleted,
        justification: item.justification,
        latest_honorary_at: item.latest_honorary_at,
        months_since_last_honorary: months_since
      });
    }

    return result;
  }, [data, search, onlyNotReajusted]);

  // Memoized statistics — single-pass accumulation
  var stats = useMemo(function() {
    var totalAmount = 0, totalAdditionals = 0, totalAmount13 = 0;
    var totalPaid = 0, totalPaid13 = 0;
    var inadAmount = 0, inadAdd = 0, inadPaid = 0, inadCount = 0;
    var ajuAmount = 0, ajuAdd = 0, ajuPaid = 0, ajuCount = 0;
    var needReajust = 0, reajustMonthsSum = 0;
    var paidCount = 0, partialCount = 0;
    var totalCount = filtered.length;
    var withAdditionalsCount = 0;
    var conciliationCount = 0, conciliationAmount = 0;

    for (var i = 0; i < totalCount; i++) {
      var item = filtered[i];
      var amt = item.amount || 0;
      var add = item.additionals_amount || 0;
      var a13 = item.amount13 || 0;
      var pd = item.paid || 0;
      var pd13 = item.paid13 || 0;

      totalAmount += amt;
      totalAdditionals += add;
      totalAmount13 += a13;
      totalPaid += pd;
      totalPaid13 += pd13;

      var itemTotal = amt + add + a13;
      var itemPaid = pd + pd13;
      if (add > 0) withAdditionalsCount++;

      if (itemPaid >= itemTotal && itemTotal > 0) {
        paidCount++;
      } else if (itemPaid > 0) {
        partialCount++;
      }

      if (item.justification === 'Pago via conciliação bancária' && itemPaid > 0) {
        conciliationCount++;
        conciliationAmount += itemPaid;
      }

      if (item.status === "Inadimplente") {
        inadAmount += amt;
        inadAdd += add;
        inadPaid += pd;
        inadCount++;
      } else if (item.status === "Ajuizado") {
        ajuAmount += amt;
        ajuAdd += add;
        ajuPaid += pd;
        ajuCount++;
      }

      if (item.months_since_last_honorary >= 12) {
        needReajust++;
        reajustMonthsSum += item.months_since_last_honorary;
      }
    }

    var grandTotal = totalAmount + totalAdditionals + totalAmount13;
    var grandPaid = totalPaid + totalPaid13;

    return {
      totalCount: totalCount,
      totalAmount: totalAmount,
      totalAdditionals: totalAdditionals,
      totalAmount13: totalAmount13,
      totalPaid: totalPaid,
      totalPaid13: totalPaid13,
      grandTotal: grandTotal,
      grandPaid: grandPaid,
      paidCount: paidCount,
      partialCount: partialCount,
      unpaidCount: totalCount - paidCount - partialCount,
      paidPercent: grandTotal > 0 ? Math.round((grandPaid / grandTotal) * 100) : 0,
      inadimplentesBalance: inadAmount + inadAdd - inadPaid,
      inadCount: inadCount,
      inadAmount: inadAmount,
      inadAdd: inadAdd,
      inadPaid: inadPaid,
      ajuizadosBalance: ajuAmount + ajuAdd - ajuPaid,
      ajuCount: ajuCount,
      ajuAmount: ajuAmount,
      ajuAdd: ajuAdd,
      ajuPaid: ajuPaid,
      withAdditionalsCount: withAdditionalsCount,
      additionalsPercent: grandTotal > 0 ? Math.round((totalAdditionals / grandTotal) * 100) : 0,
      conciliationCount: conciliationCount,
      conciliationAmount: conciliationAmount,
      conciliationPercent: grandPaid > 0 ? Math.round((conciliationAmount / grandPaid) * 100) : 0,
      needReajust: needReajust,
      reajustAvgMonths: needReajust > 0 ? Math.round(reajustMonthsSum / needReajust) : 0
    };
  }, [filtered]);

  // Memoized show13 calculation
  var show13 = useMemo(function() {
    if (stats.totalAmount13 > 0) return true;

    var decimoTerceiroSplit = user.tenant && user.tenant.decimo_terceiro_split;
    var fromMonth = moment(filter.from).format("MM");
    var toMonth = moment(filter.to).format("MM");
    var filterBy = filter.filter_by;

    var rules = {
      1: { due_at: "12", apuration_at: "11" },
      2: { due_at: ["11", "12"], apuration_at: ["10", "11"] },
      3: { due_at: ["10", "11", "12"], apuration_at: ["09", "10", "11"] }
    };

    if (!decimoTerceiroSplit || !rules[decimoTerceiroSplit]) return false;

    var rule = rules[decimoTerceiroSplit][filterBy];
    if (!rule) return false;

    if (Array.isArray(rule)) {
      return rule.includes(fromMonth) || rule.includes(toMonth);
    }
    return rule === fromMonth || rule === toMonth;
  }, [stats.totalAmount13, user.tenant, filter.from, filter.to, filter.filter_by]);

  // Memoized rowSelection
  var rowSelection = useMemo(function() {
    return {
      selectedRowKeys: selectedRowKeys,
      onChange: setSelectedRowKeys
    };
  }, [selectedRowKeys]);

  var fmtBRL = function(v) { return toBRL(v != null ? v : 0); };

  var totalTooltip = (
    <StatTooltip
      title="Resumo do Total"
      rows={[
        { label: 'Mensalidades', value: stats.totalCount + ' registros' },
        { label: 'Honorários', value: fmtBRL(stats.totalAmount) },
        { label: 'Adicionais', value: fmtBRL(stats.totalAdditionals) + ' (' + stats.withAdditionalsCount + ' com adicionais)' },
        { label: '13º Salário', value: fmtBRL(stats.totalAmount13) },
        { label: 'Total', value: fmtBRL(stats.grandTotal), bold: true, borderTop: true },
      ]}
    />
  );

  var pagoTooltip = (
    <StatTooltip
      title="Detalhes de Pagamento"
      rows={[
        { label: 'Pagos integralmente', value: stats.paidCount + ' de ' + stats.totalCount },
        { label: 'Pagos parcialmente', value: stats.partialCount + '' },
        { label: 'Sem pagamento', value: stats.unpaidCount + '', color: '#ff7875' },
        { label: '% Recebido', value: stats.paidPercent + '%', bold: true, borderTop: true, color: stats.paidPercent >= 80 ? '#95de64' : stats.paidPercent >= 50 ? '#ffc53d' : '#ff7875' },
        { label: 'Total pago', value: fmtBRL(stats.grandPaid), bold: true },
        { label: 'Via conciliação', value: stats.conciliationCount + ' (' + fmtBRL(stats.conciliationAmount) + ')', borderTop: true, color: '#52c41a' },
        { label: '% por conciliação', value: stats.conciliationPercent + '%', color: '#52c41a' },
      ]}
    />
  );

  var saldoValue = stats.grandTotal - stats.grandPaid;
  var saldoTooltip = (
    <StatTooltip
      title="Detalhes do Saldo"
      rows={[
        { label: 'Total a receber', value: fmtBRL(stats.grandTotal) },
        { label: 'Total recebido', value: fmtBRL(stats.grandPaid), color: '#95de64' },
        { label: 'Saldo mensalidades', value: fmtBRL(stats.totalAmount + stats.totalAdditionals - stats.totalPaid) },
        { label: 'Saldo 13º', value: fmtBRL(stats.totalAmount13 - stats.totalPaid13) },
        { label: 'Saldo pendente', value: fmtBRL(saldoValue), bold: true, borderTop: true, color: '#ff7875' },
      ]}
    />
  );

  var inadAjuTooltip = (
    <StatTooltip
      title="Inadimplentes & Ajuizados"
      rows={[
        { label: 'Inadimplentes', value: stats.inadCount + ' cliente(s)', color: '#ffc53d' },
        { label: '  Honorários', value: fmtBRL(stats.inadAmount) },
        { label: '  Adicionais', value: fmtBRL(stats.inadAdd) },
        { label: '  Valor pago', value: fmtBRL(stats.inadPaid), color: '#95de64' },
        { label: '  Saldo', value: fmtBRL(stats.inadimplentesBalance), bold: true, color: '#ff7875' },
        { label: 'Ajuizados', value: stats.ajuCount + ' cliente(s)', borderTop: true, color: '#d9d9d9' },
        { label: '  Honorários', value: fmtBRL(stats.ajuAmount) },
        { label: '  Adicionais', value: fmtBRL(stats.ajuAdd) },
        { label: '  Valor pago', value: fmtBRL(stats.ajuPaid), color: '#95de64' },
        { label: '  Saldo', value: fmtBRL(stats.ajuizadosBalance), bold: true, color: '#ff7875' },
      ]}
    />
  );

  var reajusteTooltip = (
    <StatTooltip
      title="Detalhes de Reajuste"
      rows={[
        { label: 'Clientes sem reajuste', value: stats.needReajust + ' de ' + stats.totalCount },
        { label: 'Tempo médio sem reajuste', value: stats.reajustAvgMonths + ' meses' },
      ]}
      description="Clientes cujo honorário não foi reajustado há mais de 12 meses. Clique para filtrar."
    />
  );

  var restoredSummary = (function() {
    var FILTER_BY_LABELS = { due_at: 'Vencimento', apuration_at: 'Competência' };
    var STATUS_LABELS = { all: 'Todos', paid: 'Pago', pendent: 'Pendente', defaulting: 'Inadimplente', processed: 'Ajuizado' };
    var parts = [];
    if (!isPeriodDefault(applied, defaults)) parts.push(getPeriodLabel(filter.from, filter.to));
    if (filter.filter_by !== defaults.filter_by) parts.push('Por: ' + (FILTER_BY_LABELS[filter.filter_by] || filter.filter_by));
    if (filter.status !== defaults.status) parts.push(STATUS_LABELS[filter.status] || filter.status);
    if (filter.customer_id !== defaults.customer_id) {
      var cust = customers.filter(function(c) { return c.id === filter.customer_id; })[0];
      parts.push(cust ? cust.cod + ' - ' + cust.name : 'Cliente filtrado');
    }
    if (onlyNotReajusted) parts.push('Sem reajuste');
    if (search) parts.push('"' + search + '"');
    return parts.join(' · ');
  })();

  return (
    <div>
      {drawerPay && (
        <DrawerMonthlyPayment
          data={drawerPay}
          setData={setDrawerPay}
          changeAmount={changeAmount}
          changePaid={changePaid}
        />
      )}

      <GenerateSummaryModal
        visible={generateSummary !== null}
        summary={generateSummary}
        onClose={function() { setGenerateSummary(null); }}
      />

      <Modal
        title="Excluir mensalidades selecionadas?"
        visible={showDeleteModal}
        onCancel={function() { setShowDeleteModal(false); }}
        footer={[
          <Button key="cancel" onClick={function() { setShowDeleteModal(false); }}>
            Cancelar
          </Button>,
          <Button
            key="confirm"
            type="danger"
            disabled={deleteConfirmText !== "excluir"}
            onClick={handleBulkDeleteConfirm}
          >
            Excluir
          </Button>
        ]}
      >
        <p>Você está prestes a excluir <strong>{selectedRowKeys.length} mensalidade(s)</strong>. Esta ação não pode ser desfeita.</p>
        <p>Digite <strong>excluir</strong> para confirmar:</p>
        <Input
          value={deleteConfirmText}
          onChange={function(e) { setDeleteConfirmText(e.target.value); }}
          placeholder="excluir"
        />
      </Modal>

      <ModuleHeaderWrapper>
        <ModuleHeader
          breadcrumbs={["Financeiro", "Mensalidades"]}
          title={"Mensalidades"}
          search={
            <Search
              value={search}
              placeholder="Pesquisar"
              onChange={handleSearchChange}
            />
          }
          center={
            <>
              <Popover
                trigger="click"
                placement="bottomRight"
                content={
                  <div style={{ width: 380 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
                      <span style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>Filtros</span>
                      {!persisted.isDefault && (
                        <a style={{ fontSize: 12, color: '#f5222d' }} onClick={handleClearFilters}>
                          Limpar tudo
                        </a>
                      )}
                    </div>

                    {/* Period */}
                    <div style={{ marginBottom: 18 }}>
                      <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Período</div>
                      <MonthPicker
                        value={moment(filter.from, 'YYYY-MM-DD').format('YYYY-MM')}
                        onChange={handleMonthChange}
                        onCustomRange={function(from, to) {
                          applyFilter({ from: from, to: to });
                        }}
                        customRangeLabel={
                          !isSingleWholeMonth(filter.from, filter.to)
                            ? getPeriodLabel(filter.from, filter.to)
                            : null
                        }
                        showTodayButton={false}
                        style={{ display: 'block', width: '100%' }}
                      />
                    </div>

                    {/* Filter by - inline buttons */}
                    <div style={{ marginBottom: 18 }}>
                      <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Filtrar por</div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {[
                          { value: 'due_at', label: 'Vencimento', icon: 'calendar' },
                          { value: 'apuration_at', label: 'Competência', icon: 'schedule' },
                        ].map(function(opt) {
                          var isActive = (filter.filter_by || 'due_at') === opt.value;
                          return (
                            <div key={opt.value}
                              onClick={function() { applyFilter({ filter_by: opt.value }); }}
                              style={{
                                flex: 1, padding: '8px 12px', borderRadius: 6, cursor: 'pointer', textAlign: 'center',
                                border: '1.5px solid ' + (isActive ? '#1890ff' : '#e8e8e8'),
                                background: isActive ? '#e6f7ff' : '#fff',
                                color: isActive ? '#1890ff' : '#595959',
                                fontSize: 13, fontWeight: isActive ? 600 : 400,
                                transition: 'all 0.2s',
                              }}
                            >
                              <Icon type={opt.icon} style={{ marginRight: 6 }} />
                              {opt.label}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Status - card options */}
                    <div style={{ marginBottom: 18 }}>
                      <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {[
                          { value: 'all', label: 'Todos', color: '#8c8c8c' },
                          { value: 'pendent', label: 'Pendente', color: '#fa8c16' },
                          { value: 'paid', label: 'Pago', color: '#52c41a' },
                          { value: 'defaulting', label: 'Inadimplente', color: '#f5222d' },
                          { value: 'processed', label: 'Ajuizado', color: '#722ed1' },
                        ].map(function(opt) {
                          var isActive = (filter.status || 'all') === opt.value;
                          return (
                            <div key={opt.value}
                              onClick={function() { applyFilter({ status: opt.value }); }}
                              style={{
                                padding: '6px 14px', borderRadius: 20, cursor: 'pointer',
                                border: '1.5px solid ' + (isActive ? opt.color : '#e8e8e8'),
                                background: isActive ? opt.color + '10' : '#fff',
                                fontSize: 12, fontWeight: isActive ? 600 : 400,
                                color: isActive ? opt.color : '#595959',
                                transition: 'all 0.2s',
                                display: 'flex', alignItems: 'center', gap: 6,
                              }}
                            >
                              <span style={{ width: 6, height: 6, borderRadius: '50%', background: opt.color }} />
                              {opt.label}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Customer */}
                    <div>
                      <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Cliente</div>
                      <Select
                        value={filter.customer_id || 'all'}
                        style={{ width: '100%' }}
                        placeholder="Todos"
                        showSearch
                        optionFilterProp="children"
                        filterOption={function(input, option) {
                          return option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                        }}
                        onChange={function(val) { applyFilter({ customer_id: val }); }}
                      >
                        <Select.Option key={0} value="all">Todos</Select.Option>
                        {customers.map(function(customer) {
                          return (
                            <Select.Option key={customer.id} value={customer.id}>
                              {customer.cod + ' - ' + customer.name}
                            </Select.Option>
                          );
                        })}
                      </Select>
                    </div>
                  </div>
                }
              >
                <Badge
                  count={countActiveFilters(applied, defaults)}
                  offset={[-4, 0]}
                  style={{ backgroundColor: '#722ed1' }}
                >
                  <Button
                    icon="bars"
                    style={{ borderRadius: 8 }}
                  >
                    Filtros
                  </Button>
                </Badge>
              </Popover>
              <Dropdown
                trigger={['click']}
                overlay={
                  <Menu>
                    <Menu.Item
                      key="generate"
                      disabled={isGenerateLoading}
                      onClick={function() { handleGenerate(stats.needReajust); }}
                    >
                      <Icon type="file-add" /> Gerar Mensalidades
                    </Menu.Item>
                  </Menu>
                }
              >
                <Button icon="plus" shape="circle" loading={isGenerateLoading} />
              </Dropdown>
            </>
          }
          advanced={
            (function() {
              var FILTER_BY_LABELS = { due_at: 'Vencimento', apuration_at: 'Competência' };
              var STATUS_LABELS = { all: 'Todos', paid: 'Pago', pendent: 'Pendente', defaulting: 'Inadimplente', processed: 'Ajuizado' };
              var isCustomRange = !isSingleWholeMonth(filter.from, filter.to);

              var pills = [
                {
                  key: 'month', label: getPeriodLabel(filter.from, filter.to),
                  color: isCustomRange ? '#722ed1' : '#1890ff', icon: 'calendar',
                  onRemove: isPeriodDefault(applied, defaults) ? undefined : handleResetPeriod,
                },
              ];

              if (filter.filter_by && filter.filter_by !== 'due_at') {
                pills.push({
                  key: 'filter_by', label: 'Por: ' + (FILTER_BY_LABELS[filter.filter_by] || filter.filter_by),
                  color: '#722ed1', icon: 'calendar',
                  onRemove: function() { applyFilter({ filter_by: 'due_at' }); },
                });
              }

              if (filter.status && filter.status !== 'all') {
                pills.push({
                  key: 'status', label: STATUS_LABELS[filter.status] || filter.status,
                  color: filter.status === 'paid' ? '#52c41a' : filter.status === 'pendent' ? '#faad14' : filter.status === 'defaulting' ? '#f5222d' : '#8c8c8c',
                  onRemove: function() { applyFilter({ status: 'all' }); },
                });
              }

              if (filter.customer_id && filter.customer_id !== 'all') {
                var cust = customers.filter(function(c) { return c.id === filter.customer_id; })[0];
                pills.push({
                  key: 'customer', label: cust ? cust.cod + ' - ' + cust.name : 'Cliente filtrado',
                  color: '#13c2c2', icon: 'user',
                  onRemove: function() { applyFilter({ customer_id: 'all' }); },
                });
              }

              if (onlyNotReajusted) {
                pills.push({
                  key: 'reajuste', label: 'Sem reajuste',
                  color: '#f5222d', icon: 'warning',
                  onRemove: function() { setOnlyNotReajusted(false); },
                });
              }

              if (search) {
                pills.push({
                  key: 'search', label: search,
                  color: '#722ed1', icon: 'search',
                  onRemove: function() { setSearch(''); },
                });
              }

              return React.createElement(FilterPillsBar, {
                pills: pills,
                onClearAll: persisted.isDefault ? undefined : handleClearFilters,
              });
            })()
          }
        />
      </ModuleHeaderWrapper>

      <RestoredFiltersNotice
        visible={persisted.wasRestored}
        summary={restoredSummary}
        onReset={handleClearFilters}
        onDismiss={persisted.dismissRestored}
      />

      <Row gutter={16} style={{ paddingTop: 10 }}>
        <Col span={4}>
          <Tooltip title={totalTooltip} placement="bottom">
            <Card
              style={{ marginBottom: "10px" }}
              bodyStyle={{ padding: "15px" }}
            >
              <Statistic
                title="Total"
                value={stats.totalAmount + stats.totalAdditionals + stats.totalAmount13}
                precision={2}
                formatter={toBRL}
              />
              {show13 && (
                <Statistic
                  title={<small>Mensalidades</small>}
                  value={stats.totalAmount}
                  precision={2}
                  formatter={toBRL}
                  valueRender={function(val) { return <small>{val}</small>; }}
                />
              )}
              {show13 && (
                <Statistic
                  title={<small>Adicionais</small>}
                  value={stats.totalAdditionals}
                  precision={2}
                  formatter={toBRL}
                  valueRender={function(val) { return <small>{val}</small>; }}
                />
              )}
              {show13 && (
                <Statistic
                  title={<small>13º Salário</small>}
                  value={stats.totalAmount13}
                  precision={2}
                  formatter={toBRL}
                  valueRender={function(val) { return <small>{val}</small>; }}
                />
              )}
            </Card>
          </Tooltip>
        </Col>
        <Col span={4}>
          <Tooltip title={pagoTooltip} placement="bottom">
            <Card
              style={{ marginBottom: "10px" }}
              bodyStyle={{ padding: "15px" }}
            >
              <Statistic
                title={
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    Pago
                    {stats.conciliationCount > 0 && (
                      <Tooltip title={stats.conciliationCount + ' pago(s) via conciliação bancária (' + fmtBRL(stats.conciliationAmount) + ' · ' + stats.conciliationPercent + '% do total pago)'}>
                        <Icon type="bank" style={{ color: '#52c41a', fontSize: 12 }} />
                      </Tooltip>
                    )}
                  </span>
                }
                value={stats.totalPaid + stats.totalPaid13}
                precision={2}
                valueStyle={{ color: "#52c41a" }}
                formatter={toBRL}
              />
              {show13 && (
                <Statistic
                  title={<small>Mensalidades & adicionais</small>}
                  value={stats.totalPaid}
                  precision={2}
                  formatter={toBRL}
                  valueRender={function(val) { return <small>{val}</small>; }}
                />
              )}
              {show13 && (
                <Statistic
                  title={<small>13º Salário</small>}
                  value={stats.totalPaid13}
                  precision={2}
                  formatter={toBRL}
                  valueRender={function(val) { return <small>{val}</small>; }}
                />
              )}
            </Card>
          </Tooltip>
        </Col>
        <Col span={4}>
          <Tooltip title={saldoTooltip} placement="bottom">
            <Card
              style={{ marginBottom: "10px" }}
              bodyStyle={{ padding: "15px" }}
            >
              <Statistic
                title="Saldo"
                value={
                  stats.totalAmount +
                  stats.totalAdditionals -
                  stats.totalPaid +
                  (stats.totalAmount13 - stats.totalPaid13)
                }
                precision={2}
                valueStyle={{ color: "#f5222d" }}
                formatter={toBRL}
              />
              {show13 && (
                <Statistic
                  title={<small>Mensalidade & adicionais</small>}
                  value={stats.totalAmount + stats.totalAdditionals - stats.totalPaid}
                  precision={2}
                  formatter={toBRL}
                  valueRender={function(val) { return <small>{val}</small>; }}
                />
              )}
              {show13 && (
                <Statistic
                  title={<small>13º Salário</small>}
                  value={stats.totalAmount13 - stats.totalPaid13}
                  precision={2}
                  formatter={toBRL}
                  valueRender={function(val) { return <small>{val}</small>; }}
                />
              )}
            </Card>
          </Tooltip>
        </Col>
        <Col span={8}>
          <Tooltip title={inadAjuTooltip} placement="bottom">
            <Card
              style={{ marginBottom: "10px" }}
              bodyStyle={{ padding: "15px" }}
            >
              <Row>
                <Col span={12}>
                  <Statistic
                    title={
                      <span>
                        <Badge status="warning" /> Inadimplentes
                      </span>
                    }
                    value={stats.inadimplentesBalance}
                    precision={2}
                    formatter={toBRL}
                    valueRender={function(val) { return <small>{val}</small>; }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title={
                      <span>
                        <Badge status="default" /> Ajuizados
                      </span>
                    }
                    value={stats.ajuizadosBalance}
                    precision={2}
                    formatter={toBRL}
                    valueRender={function(val) { return <small>{val}</small>; }}
                  />
                </Col>
              </Row>
            </Card>
          </Tooltip>
        </Col>
        <Col span={4}>
          <Tooltip title={reajusteTooltip} placement="bottom">
            <Card
              onClick={function() { setOnlyNotReajusted(!onlyNotReajusted); }}
              style={{ marginBottom: "10px" }}
              bodyStyle={{
                padding: "15px",
                cursor: "pointer",
                backgroundColor: onlyNotReajusted ? "#f5f5f5" : "white"
              }}
            >
              <Statistic
                title={
                  <>
                    <PulseBadge status="red" /> Reajuste
                  </>
                }
                value={stats.needReajust}
                valueStyle={{ color: "#f5222d" }}
              />
            </Card>
          </Tooltip>
        </Col>
      </Row>

      {isLoading ? (
        <Card style={{ marginTop: 10 }} bodyStyle={{ padding: 16 }}>
          {[0, 1, 2, 3, 4, 5, 6, 7].map(function(i) {
            return (
              <div key={i} style={{ display: 'flex', gap: 16, alignItems: 'center', padding: '10px 0', borderBottom: i < 7 ? '1px solid #f0f0f0' : 'none' }}>
                <Skeleton active title={false} paragraph={{ rows: 1, width: '80%' }} />
              </div>
            );
          })}
        </Card>
      ) : Array.isArray(data) && data.length === 0 ? (
        <Card style={{ marginTop: 20, textAlign: 'center', padding: '60px 40px', border: '1px dashed #d9d9d9' }}>
          <div style={{ fontSize: 56, color: '#bfbfbf', marginBottom: 16 }}>
            <Icon type="file-add" />
          </div>
          <h3 style={{ fontSize: 18, fontWeight: 600, color: '#262626', marginBottom: 8 }}>
            Nenhuma mensalidade em {moment(filter.from).format('MMMM/YYYY')}
          </h3>
          <p style={{ fontSize: 14, color: '#8c8c8c', maxWidth: 480, margin: '0 auto 24px' }}>
            Você ainda não gerou as mensalidades deste mês. Clique no botão abaixo para gerar automaticamente as mensalidades de todos os clientes ativos.
          </p>
          <Button
            type="primary"
            icon="file-add"
            size="large"
            loading={isGenerateLoading}
            onClick={function() { handleGenerate(stats.needReajust); }}
          >
            Gerar Mensalidades
          </Button>
          <div style={{ marginTop: 20, fontSize: 12, color: '#bfbfbf' }}>
            <Icon type="info-circle" style={{ marginRight: 4 }} />
            Você também pode acessar essa ação no botão <strong>+</strong> no topo da página.
          </div>
        </Card>
      ) : (
        <TableList
          filteredData={filtered}
          data={data}
          isLoading={isLoading}
          rowSelection={rowSelection}
          onAction={onAction}
          user={user}
          highlightedIds={highlightedIds}
          onShowReconciliation={function(id) { setReconciliationDrawerId(id); }}
        />
      )}

      <ReconciliationInfoDrawer
        visible={!!reconciliationDrawerId}
        recordId={reconciliationDrawerId}
        recordType="mensalidade"
        monthlyFilter={filter}
        onClose={function() { setReconciliationDrawerId(null); }}
      />

      <SelectionToolbar
        count={selectedRowKeys.length}
        onClear={handleClearSelection}
        itemLabel="mensalidade"
        actions={[
          { label: "Imprimir", icon: "printer", onClick: handleBulkPrint },
          { label: "Exportar", icon: "download", onClick: handleExportToExcel },
          { label: "Excluir", icon: "delete", onClick: handleBulkDelete, danger: true }
        ]}
      />
    </div>
  );
};

export default MonthlyPaymentList;
